/**
 * useIsAdvisorContext — SSOT-Hook für „befindet sich der User im Steuerberater-Kontext?"
 * (M11b Portal-Fix, Bug 1 + Bug 4).
 *
 * Liefert `true`, sobald EINES von fünf advisor-spezifischen Signalen (ODER-verknüpft)
 * greift, sonst `false`. Genutzt von `MainLayout` (Onboarding-Wizard/AppTour für Berater
 * unterdrücken; Menülink-Sichtbarkeit) — kein Copy-Paste der Bedingung.
 *
 * Flag-off-Garantie: jedes Signal ist bereits inert, wenn sein Flag off ist. Sind BEIDE
 * Flags (`TENANT_SCOPING_ENABLED`, `ADVISOR_EMAIL_INVITE_ENABLED`) off, ist das Ergebnis
 * zwangsläufig `false` — kein Verhaltensdelta zum Status quo:
 *  - (a) `activeRole` ist `'owner'` bei Flag off,
 *  - (b) `availableTenants` enthält nur den eigenen `role:'owner'`-Tenant,
 *  - (c) `pendingInvites` ist `[]` (loadPendingInvites early-return),
 *  - (d) `getPendingInviteToken()` gibt `null` bei ADVISOR_EMAIL_INVITE off,
 *  - (e) das Firestore-Feld `pendingAdvisorInviteHint` wird zusätzlich explizit hinter
 *        `ADVISOR_EMAIL_INVITE_ENABLED` geklammert (Schutz gegen ein aus einer früheren
 *        Aktivierung stale gebliebenes Feld).
 *
 * @module hooks/useIsAdvisorContext
 */

import { useAuth } from '../context/AuthContext';
import { getPendingInviteToken } from '../utils/advisorInviteToken';
import { ADVISOR_EMAIL_INVITE_ENABLED } from '../api/advisorInviteApi';

/**
 * Fail-closed Erkennung des Steuerberater-Kontexts.
 *
 * @returns `true`, wenn eines der fünf advisor-spezifischen Signale (a)–(e) greift,
 *          sonst `false`. Reines Ableiten aus bereits vorhandenem Context-/localStorage-
 *          State — kann selbst nicht werfen und löst keine zusätzlichen Reads aus.
 */
export function useIsAdvisorContext(): boolean {
  const { activeRole, availableTenants, pendingInvites, sellerData } = useAuth();

  // (a) aktive Berater-Rolle im aktuell gewählten Tenant.
  if (activeRole === 'advisor') return true;

  // (b) irgendein verfügbarer Tenant mit Berater-Membership.
  if (availableTenants.some((t) => t.role === 'advisor')) return true;

  // (c) offene (noch nicht angenommene) Einladung.
  if (pendingInvites.length > 0) return true;

  // (d) ein per Mail-Link mitgeführter Invite-Token (bereits hinter ADVISOR_EMAIL_INVITE).
  if (getPendingInviteToken() !== null) return true;

  // (e) Register-Hint aus flagPendingAdvisorInvitesOnRegister — nur wenn das Flag on ist
  //     (stale-Feld-Schutz).
  if (ADVISOR_EMAIL_INVITE_ENABLED && sellerData?.pendingAdvisorInviteHint === true) {
    return true;
  }

  return false;
}
